/**
 * lib/installed-menu-convert.ts — konwersja menu modułu (drzewo `InstalledMenuItem`
 * z GET /my/installed-modules) na hierarchię własnego układu (`MenuItem[]`).
 *
 * Użycie: „Duplikuj" na menu modułowym w galerii edytora menu tenanta — kopia
 * fragmentu staje się edytowalnym układem klienta. Mapowanie:
 *   system/template → pozycja `module` po slugu (renderer rozwiązuje na żywo;
 *                     wygasły moduł = pozycja nieaktywna — decyzja D2); przypisania widoku
 *                     (`viewRef`/`listRef`/`view`) przechodzą do kopii,
 *   link            → `custom_link` (trasa wewnętrzna lub URL zewnętrzny); Ustawienia z menu
 *                     modułu (`systemItem`) → `system_item` „settings",
 *   pozycja nieaktywna (cel z niewykupionego modułu) kopiuje się jak aktywna — własne menu
 *                     klienta rozwiązuje aktywność na żywo,
 *   section         → `section` z przemapowanymi dziećmi,
 *   separator       → `separator`.
 * Etykiety i ikony przenosimy jawnie — kopia wygląda jak oryginał nawet bez
 * lookupów w źródłach (np. w podglądzie).
 */

import {
  DOCUMENT_TYPE_FILTER_FIELD,
  extractDocumentTypeIdsFromBaseFilters,
  type MenuItem,
  type MenuItemView,
} from '@saas/shared';

import type { InstalledMenuItem } from '@/components/Sidebar';

export function convertInstalledItemsToMenuItems(items: InstalledMenuItem[]): MenuItem[] {
  const out: MenuItem[] = [];
  for (const item of items) {
    switch (item.kind) {
      case 'system':
      case 'template': {
        // Zawężenie do typów dokumentu (legacy `documentTypeId` z encji document_class
        // + `documentTypeIds` z filtra stałego fragmentu) — kopia w OWN layoucie musi
        // dziedziczyć to samo zawężenie, inaczej duplikat traci filtr typu dokumentu (leak
        // odwrotny do B4: tu chcemy PRZENIEŚĆ narrowing, nie zablokować go).
        const narrowIds = [
          ...new Set([
            ...(item.documentTypeIds ?? []),
            ...(item.documentTypeId ? [item.documentTypeId] : []),
          ]),
        ];
        // `item.view` (stałe filtry/sortowanie pozycji z menu modułu) przechodzi do kopii w całości
        // i niesie zawężenie z filtra stałego fragmentu. Brakuje w nim tylko zawężenia spoza
        // `view.baseFilters` (legacy `documentTypeId` z encji document_class) — wtedy filtr typu
        // dokumentu składamy z sumy typów widoku i `narrowIds` (jeden filtr `in`, bez duplikatów;
        // typy z samego widoku nie giną nawet gdy `documentTypeIds` ich nie powtarza).
        const viewTypeIds = extractDocumentTypeIdsFromBaseFilters(item.view?.baseFilters);
        const view: MenuItemView | undefined = narrowIds.every((id) => viewTypeIds.includes(id))
          ? item.view
          : {
              ...item.view,
              baseFilters: [
                ...(item.view?.baseFilters ?? []).filter(
                  (f) => !(f.field === DOCUMENT_TYPE_FILTER_FIELD && f.operator === 'in'),
                ),
                {
                  field: DOCUMENT_TYPE_FILTER_FIELD,
                  operator: 'in' as const,
                  value: [...new Set([...viewTypeIds, ...narrowIds])].join(','),
                },
              ],
            };
        out.push({
          id: crypto.randomUUID(),
          type: 'module',
          moduleCode: item.slug,
          label: item.name,
          iconName: item.icon ?? undefined,
          // Przypisania widoku (układy z menu modułu, stałe filtry) — wskaźniki są w formacie
          // klienta, więc kopia otwiera kartotekę w tych samych układach.
          ...(item.viewRef ? { viewRef: item.viewRef } : {}),
          ...(item.listRef ? { listRef: item.listRef } : {}),
          ...(view ? { view } : {}),
        });
        break;
      }
      case 'link':
        // Ustawienia wstawione do menu modułu (kreator ITE) → z powrotem element systemowy.
        if (item.systemItem === 'settings') {
          out.push({
            id: crypto.randomUUID(),
            type: 'system_item',
            itemKey: 'settings',
            label: item.name,
            iconName: item.icon ?? undefined,
          });
          break;
        }
        if (!item.url) break;
        out.push({
          id: crypto.randomUUID(),
          type: 'custom_link',
          url: item.url,
          label: item.name,
          iconName: item.icon ?? undefined,
          external: item.external ?? false,
        });
        break;
      case 'section':
        out.push({
          id: crypto.randomUUID(),
          type: 'section',
          label: item.name,
          iconName: item.icon ?? undefined,
          children: convertInstalledItemsToMenuItems(item.children ?? []),
        });
        break;
      case 'separator':
        out.push({ id: crypto.randomUUID(), type: 'separator' });
        break;
    }
  }
  return out;
}

/** Liczba pozycji-liści (bez sekcji/separatorów) — licznik w galerii menu modułów. */
export function countInstalledLeaves(items: InstalledMenuItem[]): number {
  let count = 0;
  for (const item of items) {
    if (item.kind === 'section') {
      count += countInstalledLeaves(item.children ?? []);
    } else if (item.kind !== 'separator') {
      count += 1;
    }
  }
  return count;
}
